<template>
  <div>
    <div class="d-flex mb-1">
      <PhysicianPickerWindow @physician-selected="onPhysicianSelected"/>
      <InputText
        v-model="cegatId"
        class="ml-2"
        type="search"
        :placeholder="$t('CeGaT ID')"
        hide-details="auto"
        clearable
        @keydown="watchUserSubmit"
        @click:clear="clearCegatId"
      />
      <Button
        class="ml-2"
        @click="loadPatientsByCegatId"
      >
        {{ $t("Load patient") }}
      </Button>
    </div>
    <div
      v-if="selectedPhysician.userId"
      class="mb-3 font-weight-bold"
    >
      {{ $t(`Patients for`) }}: {{ selectedPhysician.firstName }}
      {{ selectedPhysician.lastName }}
      <Button
        color="red"
        class="ml-2 btn-fab--x-small btn-no-hover-overlay"
        :icon-btn="true"
        :title="$t('Clear physician')"
        size="x-small"
        variant="outlined"
        density="comfortable"
        @click="clearPhysician"
      >
        <v-icon color="red">mdi-close</v-icon>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue"
import Button from "@/components/input-components/Button.vue"
import InputText from "@/components/input-components/InputText.vue"
import PhysicianPickerWindow from "@/components/patients/PhysicianPickerWindow.vue"
import {Physician} from "@/entities/physician"
import {usePatientsStore} from "@/stores/patients"
import {usePhysiciansStore} from "@/stores/physicians"
import {useOrdersStore} from "@/stores/orders"

defineOptions({name: "PatientsToolbarInternal"})

const cegatId = ref("")
const patientsStore = usePatientsStore()
const physiciansStore = usePhysiciansStore()
const ordersStore = useOrdersStore()

const selectedPhysician = computed<Physician>(() =>
    physiciansStore.getPhysician ?? {
      userId: "",
      firstName: "",
      lastName: ""
    }
)

function watchUserSubmit(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    loadPatientsByCegatId()
  }
}


async function loadPatientsByPhysician(physician: Physician) {
  await clearCegatId()
  await patientsStore.fetchPatientsByPhysicianId(physician.userId)
  physiciansStore.setPhysician(physician)
  await ordersStore.fetchOrdersByPhysicianId(physician.userId)
}

async function loadPatientsByCegatId() {
  resetFilters()
  if (cegatId.value) {
    await patientsStore.fetchPatientsByCegatId(cegatId.value)
  } else {
    patientsStore.resetPatients()
    await patientsStore.fetchPatients(true)
  }
}

async function onPhysicianSelected(physician: Physician) {
  await loadPatientsByPhysician(physician)
}

async function clearPhysician() {
  physiciansStore.resetPhysician()
  patientsStore.resetPatients()
  await patientsStore.fetchPatients(true)
  resetFilters()
}

async function clearCegatId() {
  cegatId.value = ""
  patientsStore.resetPatients()
  await patientsStore.fetchPatients(true)
  resetFilters()
}

function resetFilters() {
  patientsStore.searchCegatId = ""
  patientsStore.searchInput = ""
}

onMounted(async () => {
  await patientsStore.fetchPatientsSexes();
  if (selectedPhysician.value.userId) {
    await loadPatientsByPhysician(selectedPhysician.value)
  } else {
    await patientsStore.fetchPatients(true)
  }
})
</script>
